<template>
  <div class="auth-page">
    <div class="auth-bg"></div>
    <div class="auth-grid"></div>

    <div class="auth-container">
      <div class="auth-card">
        <!-- Header -->
        <div class="auth-header">
          <div class="auth-logo">
            <div class="auth-logo-icon">CS</div>
            <span class="auth-logo-text">Casino<span class="accent">Script</span></span>
          </div>
          <h1 class="auth-title">{{ $t('auth.forgot_password_title') }}</h1>
          <p class="auth-subtitle">{{ $t('auth.forgot_password_subtitle') }}</p>
        </div>

        <!-- Success Message -->
        <div v-if="isSubmitted" class="auth-success">
          <div class="success-icon-wrap">
            <svg viewBox="0 0 24 24" fill="currentColor" class="success-icon">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          </div>
          <h2 class="success-title">{{ $t('auth.check_email_title') }}</h2>
          <p class="success-text">{{ $t('auth.check_email_text') }}</p>
          <p class="success-hint">
            {{ $t('auth.check_email_hint') }}
            <button type="button" class="resend-link" @click="resetForm">{{ $t('auth.try_again') }}</button>.
          </p>
          <router-link to="/login" class="btn-back-login">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
            </svg>
            {{ $t('auth.back_to_sign_in') }}
          </router-link>
        </div>

        <!-- Forgot Password Form -->
        <template v-else>
          <!-- Error Alert -->
          <div v-if="errorMessage" class="auth-error">
            <span class="auth-error-icon">!</span>
            <span>{{ errorMessage }}</span>
          </div>

          <form class="auth-form" @submit.prevent="handleSubmit">
            <!-- Email -->
            <div class="form-group">
              <label class="form-label" for="email">{{ $t('auth.email_label') }}</label>
              <div class="form-input-wrap">
                <svg class="form-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                <input
                  id="email"
                  v-model="email"
                  type="email"
                  class="form-input"
                  :placeholder="$t('auth.email_placeholder')"
                  required
                  autocomplete="email"
                />
              </div>
            </div>

            <!-- Submit Button -->
            <button type="submit" class="btn-signin" :disabled="isLoading">
              <span v-if="isLoading" class="btn-spinner"></span>
              <span v-else>{{ $t('auth.send_reset_link') }}</span>
            </button>
          </form>

          <!-- Footer -->
          <div class="auth-footer">
            {{ $t('auth.remember_password') }} <router-link to="/login">{{ $t('auth.sign_in') }}</router-link>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'

const { t } = useI18n()
const email = ref('')
const isLoading = ref(false)
const isSubmitted = ref(false)
const errorMessage = ref('')

function resetForm() {
  isSubmitted.value = false
  errorMessage.value = ''
  email.value = ''
}

async function handleSubmit() {
  errorMessage.value = ''
  isLoading.value = true

  try {
    await apiClient.post('/api/auth/forgot-password', {
      email: email.value,
    })

    isSubmitted.value = true
  } catch (e: unknown) {
    const axiosError = e as { response?: { data?: { message?: string; errors?: Record<string, string[]> }; status?: number } }
    const status = axiosError.response?.status

    if (status === 422) {
      const errors = axiosError.response?.data?.errors
      if (errors?.email) {
        errorMessage.value = errors.email[0]
      } else {
        errorMessage.value = axiosError.response?.data?.message || t('auth.forgot_error_invalid_email')
      }
    } else if (status === 429) {
      errorMessage.value = t('auth.forgot_error_rate_limited')
    } else {
      errorMessage.value = t('auth.error_generic')
    }
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-primary);
  overflow: hidden;
  position: relative;
}

.auth-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  background-image:
    radial-gradient(ellipse at 20% 50%, rgba(0, 231, 1, 0.05) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 20%, rgba(20, 117, 225, 0.04) 0%, transparent 50%);
  pointer-events: none;
}

.auth-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(0, 231, 1, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 231, 1, 0.02) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
}

.auth-container {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 420px;
  padding: var(--space-5);
}

.auth-card {
  background: linear-gradient(135deg, rgba(26, 44, 56, 0.95) 0%, rgba(33, 55, 67, 0.85) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-xl);
  padding: var(--space-10);
  backdrop-filter: blur(20px);
  box-shadow: var(--shadow-lg);
  animation: slideUp 0.6s ease both;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header */
.auth-header {
  text-align: center;
  margin-bottom: var(--space-8);
}

.auth-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  margin-bottom: var(--space-5);
}

.auth-logo-icon {
  width: 40px;
  height: 40px;
  background: var(--accent-primary);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000;
  font-size: 14px;
  font-weight: 700;
  font-family: var(--font-family-display);
  box-shadow: var(--shadow-glow-green);
}

.auth-logo-text {
  font-family: var(--font-family-display);
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
}

.auth-logo-text .accent {
  color: var(--accent-primary);
}

.auth-title {
  font-size: var(--font-size-h1);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin-bottom: var(--space-2);
}

.auth-subtitle {
  font-size: var(--font-size-body);
  color: var(--text-tertiary);
}

/* Success State */
.auth-success {
  text-align: center;
}

.success-icon-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: var(--space-4);
}

.success-icon {
  width: 56px;
  height: 56px;
  color: var(--accent-primary);
}

.success-title {
  font-size: var(--font-size-h2);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin-bottom: var(--space-3);
}

.success-text {
  font-size: var(--font-size-body);
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: var(--space-3);
}

.success-hint {
  font-size: var(--font-size-sm);
  color: var(--text-tertiary);
  margin-bottom: var(--space-6);
}

.resend-link {
  background: none;
  border: none;
  color: var(--accent-secondary);
  font-size: var(--font-size-sm);
  font-family: var(--font-family-primary);
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  transition: color var(--transition-fast);
}

.resend-link:hover {
  color: var(--text-primary);
}

.btn-back-login {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--accent-secondary);
  text-decoration: none;
  font-size: var(--font-size-body);
  transition: color var(--transition-fast);
}

.btn-back-login:hover {
  color: var(--text-primary);
}

.btn-back-login svg {
  width: 16px;
  height: 16px;
}

/* Error Alert */
.auth-error {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: var(--color-danger-bg);
  border: 1px solid var(--color-danger-border);
  border-radius: var(--radius-md);
  color: var(--color-danger-light);
  font-size: var(--font-size-body);
  margin-bottom: var(--space-4);
}

.auth-error-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: var(--radius-full);
  background: var(--color-danger);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Form */
.auth-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-secondary);
}

.form-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.form-icon {
  position: absolute;
  left: 12px;
  width: 16px;
  height: 16px;
  color: var(--text-tertiary);
  pointer-events: none;
}

.form-input {
  width: 100%;
  background: rgba(15, 25, 35, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-md);
  padding: 12px 16px;
  padding-left: 40px;
  color: var(--text-primary);
  font-size: var(--font-size-body);
  font-family: var(--font-family-primary);
  transition: all var(--transition-fast);
}

.form-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
  background: rgba(15, 25, 35, 1);
}

.form-input::placeholder {
  color: var(--text-tertiary);
}

/* Submit Button */
.btn-signin {
  width: 100%;
  padding: 12px 16px;
  background: var(--accent-primary);
  color: var(--color-on-accent);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-bold);
  font-family: var(--font-family-primary);
  border-radius: var(--radius-md);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast);
  letter-spacing: 0.02em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-height: 44px;
}

.btn-signin:hover:not(:disabled) {
  background: var(--accent-primary-hover);
  box-shadow: var(--shadow-glow-green);
  transform: translateY(-2px);
}

.btn-signin:active:not(:disabled) {
  transform: translateY(0);
}

.btn-signin:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-top-color: #000;
  border-radius: var(--radius-full);
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Footer */
.auth-footer {
  text-align: center;
  font-size: 13px;
  color: var(--text-tertiary);
}

.auth-footer a {
  color: var(--accent-secondary);
  text-decoration: none;
  transition: all var(--transition-fast);
}

.auth-footer a:hover {
  color: var(--text-primary);
  text-decoration: underline;
}

/* Responsive */
@media (max-width: 480px) {
  .auth-card {
    padding: 28px;
  }

  .auth-title {
    font-size: 20px;
  }
}
</style>
